/** Pure state <-> daily-config mapping for the trading-controls form
 * (extracted from StrategyControlsCard to stay under the file-length lint
 * limit). Hydrates the form from the persisted config with day-plan /
 * engine-default fallbacks, and builds the save payload. */

import { dayPlan, type DayPlan, type Instrument } from "@/lib/instrument";
import type {
  ExitMode,
  StrategyDailyConfig,
  StrategyDailyConfigInput,
  StrikeMoneyness,
  SupertrendSides,
} from "@/lib/strategyDailyConfigApi";
import type { Strategy, StrategyType } from "@/types/strategy";
import { type Bias } from "@/lib/strategyDailyConfigApi";
import { type Itm } from "./StrategyControlsHeader";

export interface ControlState {
  bias: Bias;
  instrument: Instrument;
  itm: Itm;
  lots: number;
  initialSl: number;
  trailTrigger: number;
  trailAmount: number;
  /** Rung-1 cost buffer (2026-07-15 ladder rework). */
  trailSlippage: number;
  windowStart: string;
  windowEnd: string;
  dedupSeconds: number;
  /** Shadow-exit simulation inputs (P50). */
  targetPivot: number;
  sweepPivot: number;
  /** Supertrend-only (ST-5); ignored for bias strategies. */
  exitMode: ExitMode;
  maxEntries: number;
  atrPeriod: number;
  factor: number;
  lowerTf: number;
  sides: SupertrendSides;
  /** Strike moneyness (ST-6, supertrend); `itm` above carries the depth. */
  strikeMoneyness: StrikeMoneyness;
  /** BB bottom-buy knobs (BB-6/8); ignored for other engines. */
  bbLength: number;
  bbMult: number;
  rsiEnabled: boolean;
  rsiLength: number;
  rsiOversold: number;
  greenBodyPct: number;
  dojiBodyPct: number;
  triggerValidBars: number;
  instantEntry: boolean;
}

/** Pine-script defaults for the BB knobs — the fallback for any NULL
 * column, and the harmless filler that keeps `ControlState` fully
 * populated for the non-BB engines (which never read or post them). */
const BB_KNOB_DEFAULTS = {
  bbLength: 20,
  bbMult: 2.0,
  rsiEnabled: true,
  rsiLength: 14,
  // 32 since 2026-07-28 evening — the middle ground (30 gave a silent
  // session; see RSI_OVERSOLD in bollinger_bottom.py for the counts).
  rsiOversold: 32,
  greenBodyPct: 0.5,
  dojiBodyPct: 0.1,
  // Default 5 since 2026-07-27 (operator request; was the Pine 3).
  triggerValidBars: 5,
  // ON by default (2026-07-27): a strong-green signal candle (body ≥ 70%,
  // upper wick ≤ 10%) enters at its own close, skipping the cross wait.
  instantEntry: true,
} as const;

export function controlsFor(
  strategy: Strategy,
  plan: DayPlan,
  saved: StrategyDailyConfig | null,
): ControlState {
  // Hydrate the form from what's persisted server-side. Falls back to
  // day-plan defaults only when the operator hasn't saved today's config
  // yet. This fixes the "PUT reverts to CALL on refresh" bug where the
  // form was always initialising to CALL regardless of what was stored.
  const {
    // Default to BOTH (2026-08-01 — was NO_TRADE) to match the morning
    // auto-seed. On this webhook path BOTH is still not tradable, so the
    // day stays opt-in: the operator picks CALL/PUT to arm it.
    bias = "BOTH",
    itm_offset = plan.itm,
    lots = plan.instrument.defaultLots,
    sl_offset_points = plan.instrument.defaultSlPoints,
    trail_trigger_points = plan.instrument.defaultTrailTrigger,
    trail_amount_points = plan.instrument.defaultTrailAmount,
    trail_slippage_points = plan.instrument.defaultTrailSlippage,
  } = saved ?? {};
  return {
    bias,
    instrument: plan.instrument.name,
    itm: itm_offset,
    lots,
    initialSl: sl_offset_points,
    trailTrigger: trail_trigger_points,
    trailAmount: trail_amount_points,
    trailSlippage: trail_slippage_points,
    windowStart: strategy.trading_window_start.slice(0, 5),
    windowEnd: strategy.trading_window_end.slice(0, 5),
    dedupSeconds: strategy.dedup_window_seconds,
    ...swingDefaults(saved),
    ...engineDefaults(strategy.strategy_type, saved),
  };
}

type EngineDefaults = Pick<
  ControlState,
  | "exitMode"
  | "maxEntries"
  | "atrPeriod"
  | "factor"
  | "lowerTf"
  | "sides"
  | "strikeMoneyness"
  | "bbLength"
  | "bbMult"
  | "rsiEnabled"
  | "rsiLength"
  | "rsiOversold"
  | "greenBodyPct"
  | "dojiBodyPct"
  | "triggerValidBars"
  | "instantEntry"
>;

/** Per-engine daily-config fields, with each engine's own defaults. Bias
 * strategies get the same shape as supertrend (harmless — those fields
 * are ignored server-side for bias) so `ControlState` is always fully
 * populated regardless of type. */
function engineDefaults(type: StrategyType, saved: StrategyDailyConfig | null): EngineDefaults {
  if (type === "bb_bottom_buy") return bbBottomDefaults(saved);
  return fillerDefaults(saved);
}

/** BB bottom-buy daily-config fields (BB-7). Shares the option-chart
 * engine shape: TRAIL exit + the supertrend-style strike plan (sides +
 * moneyness). It reads NONE of atr/factor/lowerTf/maxEntries/pivot/liqGrab
 * (those stay ControlState filler, never posted); the BB/RSI/pattern knobs
 * get their own inputs in BB-8. Note maxEntries filler is irrelevant — BB
 * has no re-entry cap. */
function bbBottomDefaults(saved: StrategyDailyConfig | null): EngineDefaults {
  if (saved === null) {
    return {
      exitMode: "TRAIL", // BB's whole exit model is the P47 ladder (gotcha 1)
      maxEntries: 20,
      atrPeriod: 10,
      factor: 3.0,
      lowerTf: 1,
      sides: "BOTH",
      strikeMoneyness: "ITM",
      ...BB_KNOB_DEFAULTS, // the REAL knobs for BB (Pine defaults until saved)
    };
  }
  return {
    exitMode: saved.exit_mode,
    maxEntries: saved.max_entries_per_day ?? 20,
    atrPeriod: saved.atr_period ?? 10,
    factor: saved.factor ?? 3.0,
    lowerTf: saved.lower_tf ?? 1,
    sides: saved.sides,
    strikeMoneyness: saved.strike_moneyness,
    // atr/factor/pivot/liqGrab are filler here; BB never reads them.
    // BB knobs hydrate from the saved config (NULL -> the Pine default).
    ...bbKnobsFrom(saved),
  };
}

/** The eight BB knobs from a saved config, each NULL falling back to the
 * Pine default. Split out to keep `bbBottomDefaults` under the complexity
 * gate. */
function bbKnobsFrom(
  saved: StrategyDailyConfig,
): Pick<EngineDefaults, keyof typeof BB_KNOB_DEFAULTS> {
  return {
    bbLength: saved.bb_length ?? BB_KNOB_DEFAULTS.bbLength,
    bbMult: saved.bb_mult ?? BB_KNOB_DEFAULTS.bbMult,
    rsiEnabled: saved.rsi_enabled ?? BB_KNOB_DEFAULTS.rsiEnabled,
    rsiLength: saved.rsi_length ?? BB_KNOB_DEFAULTS.rsiLength,
    rsiOversold: saved.rsi_oversold ?? BB_KNOB_DEFAULTS.rsiOversold,
    greenBodyPct: saved.green_body_pct ?? BB_KNOB_DEFAULTS.greenBodyPct,
    dojiBodyPct: saved.doji_body_pct ?? BB_KNOB_DEFAULTS.dojiBodyPct,
    triggerValidBars: saved.trigger_valid_bars ?? BB_KNOB_DEFAULTS.triggerValidBars,
    instantEntry: saved.instant_entry ?? BB_KNOB_DEFAULTS.instantEntry,
  };
}

/** Every other type: the shared option-engine fields as FILLER, so
 * `ControlState` is fully populated. No surviving engine reads or posts
 * them (they were supertrend's, retired 2026-09-14). */
function fillerDefaults(saved: StrategyDailyConfig | null): EngineDefaults {
  if (saved === null) {
    return {
      // TRAIL by default for the option-chart engines (2026-07-13).
      exitMode: "TRAIL",
      maxEntries: 20,
      atrPeriod: 10,
      // Factor default 2.0 since 2026-07-27 (operator request; was 3.0).
      factor: 2.0,
      // Default 3 (-> higher 5) since 2026-08-09 (operator; was 1 -> 3).
      lowerTf: 3,
      sides: "BOTH",
      strikeMoneyness: "ITM",
      ...BB_KNOB_DEFAULTS, // filler — supertrend never reads or posts these
    };
  }
  return {
    exitMode: saved.exit_mode,
    maxEntries: saved.max_entries_per_day ?? 20,
    atrPeriod: saved.atr_period ?? 10,
    factor: saved.factor ?? 2.0,
    // NULL falls back to the engine default, which is 3 since 2026-08-09.
    lowerTf: saved.lower_tf ?? 3,
    sides: saved.sides,
    strikeMoneyness: saved.strike_moneyness,
    ...BB_KNOB_DEFAULTS,
  };
}

/** UT Bot + STC daily-config fields. v2 (2026-07-13): `sides` and
 * `strikeMoneyness` are REAL controls now (supertrend-style strike plan
 * + sides filter on the watched premium pair); the timeframe is fixed in
 * the engine (5-min since 2026-08-09) so `lowerTf` is vestigial, and
 * `atrPeriod`/`factor` remain harmless placeholders this engine never
 * reads or sends. */

function swingDefaults(
  saved: StrategyDailyConfig | null,
): Pick<ControlState, "targetPivot" | "sweepPivot"> {
  return {
    targetPivot: saved?.target_pivot ?? 10,
    sweepPivot: saved?.sweep_pivot ?? 5,
  };
}

export function defaultControls(
  strategy: Strategy,
  saved: StrategyDailyConfig | null,
): ControlState {
  return controlsFor(strategy, dayPlan(new Date()), saved);
}

export function buildPayload(
  vars: ControlState,
  strategyType: StrategyType,
): StrategyDailyConfigInput {
  const base: StrategyDailyConfigInput = {
    bias: vars.bias,
    lots: vars.lots,
    sl_offset_points: vars.initialSl,
    trail_trigger_points: vars.trailTrigger,
    trail_amount_points: vars.trailAmount,
    trail_slippage_points: vars.trailSlippage,
    itm_offset: vars.itm,
    target_pivot: vars.targetPivot,
    sweep_pivot: vars.sweepPivot,
  };
  if (strategyType === "bb_bottom_buy") {
    return bbPayload(base, vars);
  }
  return base;
}

/** BB bottom-buy save payload. `exit_mode` MUST be posted: the request
 * schema defaults it to FIXED, so omitting it would clobber the TRAIL seed
 * and the P47 ladder — BB's only exit — would never run (gotcha 1).
 * rsi_enabled=false is the meaningful knob: it turns the filter OFF. */
function bbPayload(base: StrategyDailyConfigInput, vars: ControlState): StrategyDailyConfigInput {
  return {
    ...base,
    exit_mode: vars.exitMode,
    sides: vars.sides,
    strike_moneyness: vars.strikeMoneyness,
    bb_length: vars.bbLength,
    bb_mult: vars.bbMult,
    rsi_enabled: vars.rsiEnabled,
    rsi_length: vars.rsiLength,
    rsi_oversold: vars.rsiOversold,
    green_body_pct: vars.greenBodyPct,
    doji_body_pct: vars.dojiBodyPct,
    trigger_valid_bars: vars.triggerValidBars,
    instant_entry: vars.instantEntry,
  };
}
